/**
 * Input policy for edit mode (pure, unit tested): who draws — Apple Pencil, finger or mouse — and
 * which touches are ignored (palm rejection).
 *
 *  - Settings 'kgr.fingerDraw': 'finger' → a finger always draws; 'pen' → only a pen draws;
 *    'auto' → one finger draws until a pen has been seen on this device, then pen only.
 *  - Fingers that do not draw "navigate": they scroll natively (page touch-action pan-x pan-y),
 *    but a quick tap still works for the tap tools (Texti, Tákn, Tengill, Velja) — also on a
 *    pen-only device, so a finger can place a symbol with the Pencil put down. Palms are filtered
 *    by the palm window and the tap slop/time limits.
 *  - A pen draws only while its tip touches (buttons & 1) — Pencil hover is ignored.
 *  - Touches are ignored while a pen is down and for PALM_WINDOW_MS after it lifts.
 *  - The selection (frame + handles) can be dragged, and own marks tap-selected, with Penni,
 *    Yfirstrikun, Texti, Tákn and Velja: by a pen/mouse always; by a finger only when fingers draw —
 *    on a Pencil-only device a finger never selects or moves a mark with those tools (a palm resting
 *    on a just-placed text or symbol must not nudge it), it scrolls, also over the selection, and
 *    its taps in Texti / Tákn work as they always did. Velja keeps its finger taps and drags.
 *  - The same pointers may LONG-PRESS an own mark to select and drag it (routing.ts armsLongPress):
 *    LONG_PRESS_MS held still (a Pencil or mouse within LONG_PRESS_SLOP_PX, a finger within the tap
 *    slop) — far longer than a dot, an accent or a breath mark, and a slowly drawn one moves.
 */
import type { FingerDrawPref } from '../screens/settings/fingerDraw';
import type { ToolId } from './tools';

export const PALM_WINDOW_MS = 300;
/** Max travel (CSS px) and duration (ms) of a tap. */
export const TAP_SLOP_PX = 10;
export const TAP_MAX_MS = 700;
/** A press that stays still (LONG_PRESS_SLOP_PX) this long on an own mark selects it (routing.ts). */
export const LONG_PRESS_MS = 500;
/**
 * How far (CSS px from where it went down) a long press may drift: a resting Pencil's tremor (1–2 px)
 * keeps it, a slowly drawn dot, tick or breath mark does not. A finger's contact point jitters more,
 * so it keeps the tap slop.
 */
export const LONG_PRESS_SLOP_PX = 4;

/** Drift allowed to a long press by this pointer (LONG_PRESS_SLOP_PX; a finger TAP_SLOP_PX). */
export function longPressSlopPx(pointerType: string): number {
  return pointerType === 'touch' ? TAP_SLOP_PX : LONG_PRESS_SLOP_PX;
}

export type FingerRole = 'draw' | 'navigate';
export type PointerAction = 'drag' | 'tap' | 'ignore';

export function fingersDraw(pref: FingerDrawPref, penSeen: boolean): boolean {
  return pref === 'finger' || (pref === 'auto' && !penSeen);
}

/** First pen contact in 'auto' mode switches the device to pen-only (with a one-time toast). */
export function switchesToPenOnly(pref: FingerDrawPref, penSeen: boolean, pointerType: string): boolean {
  return pref === 'auto' && !penSeen && pointerType === 'pen';
}

/**
 * What a single finger DRAG does on a page. Only the stroke tools (Penni, Yfirstrikun, Strokleður)
 * take one-finger drags; with the tap tools (Texti, Tákn, Tengill, Velja) and in the connector
 * "find the target" step one finger scrolls natively and taps still act.
 */
export function fingerRole(tool: ToolId, fingersDrawNow: boolean, choosingTarget: boolean): FingerRole {
  if (choosingTarget || tool === 'connector' || tool === 'select' || tool === 'text' || tool === 'stamp') return 'navigate';
  return fingersDrawNow ? 'draw' : 'navigate';
}

export interface PenState {
  /** A pen pointer is currently down. */
  penDown: boolean;
  /** performance.now() of the last pen lift (−Infinity when never). */
  lastPenUpAt: number;
}

export function palmRejected(pointerType: string, pen: PenState, now: number, windowMs = PALM_WINDOW_MS): boolean {
  if (pointerType !== 'touch') return false;
  return pen.penDown || now - pen.lastPenUpAt < windowMs;
}

/** Tools with which own marks are tapped to select them and the selection is dragged directly. */
export const GRAB_TOOLS: readonly ToolId[] = ['pen', 'highlighter', 'text', 'stamp', 'select'];

/**
 * May a finger select own marks and drag the selection with this tool (else the selection handles
 * let the page scroll)? Velja always (as before); the other grab tools only when fingers draw.
 */
export function fingerGrabsSelection(tool: ToolId, fingersDrawNow: boolean): boolean {
  return tool === 'select' || (fingersDrawNow && GRAB_TOOLS.includes(tool));
}

/**
 * May a tap by this pointer select an own mark? A pen / mouse always; a finger where it may grab
 * the selection, and with Tengill (its pins, as before). A finger on a Pencil-only device in Texti /
 * Tákn taps as it always did: it opens an own text, or creates a text / places a symbol.
 */
export function tapMaySelect(pointerType: string, tool: ToolId, fingersDrawNow: boolean): boolean {
  if (pointerType !== 'touch') return true;
  return tool === 'connector' || fingerGrabsSelection(tool, fingersDrawNow);
}

export interface PointerDownInput {
  pointerType: string;
  buttons: number;
  tool: ToolId;
  role: FingerRole;
  pen: PenState;
  now: number;
  /** Fingers draw (setting + Pencil seen); with false a finger never grabs the selection (Velja aside). */
  fingersDraw: boolean;
  /** The pointer went down on the selection (its frame, move area or a handle). */
  onSelection: boolean;
  /** The connector tool is waiting for its target tap. */
  choosingTarget: boolean;
}

/**
 * Classifies a pointerdown on a page:
 *  'drag'   – start a stroke / segment / erase / selection drag (pointer captured),
 *  'tap'    – act on pointerup if the pointer did not travel (text, symbols, connector, select),
 *  'ignore' – palm, hovering pen, secondary buttons, or a navigating finger.
 */
export function classifyPointerDown(i: PointerDownInput): PointerAction {
  const { pointerType, tool } = i;
  if (palmRejected(pointerType, i.pen, i.now)) return 'ignore';
  const precise = pointerType === 'pen' || pointerType === 'mouse';
  if (precise && (i.buttons & 1) === 0) return 'ignore';
  if (i.choosingTarget) return 'tap';
  if (i.onSelection && GRAB_TOOLS.includes(tool) && (precise || fingerGrabsSelection(tool, i.fingersDraw))) return 'drag';
  if (tool === 'select') return precise ? 'drag' : 'tap';
  if (tool === 'connector' || tool === 'text' || tool === 'stamp') return 'tap';
  return precise || i.role === 'draw' ? 'drag' : 'ignore';
}

/** Did a pointer stay within the tap slop and time? */
export function isTap(dx: number, dy: number, ms: number): boolean {
  return Math.hypot(dx, dy) <= TAP_SLOP_PX && ms <= TAP_MAX_MS;
}
